// Free accounts in the web app (2026-10-08): who's signed in (/api/me), the sign-up sheet, the panel meter, the gates
// and the account page (#/account). Sign-up and sign-in are the same form: an email address gets a link
// (src/accounts.ts); opening it signs this browser in for 180 days.
//
// The gate (/api/config `gate`, SIGNUP_GATE on the server) asks for an account at moments that show what it's for:
// the forecast's workings, following an area, the report, downloads, Ask and the listing paste past their free uses,
// and the meter (a dismissible prompt at the 10th area opened in a day without an account; no blur unless the server
// sets a wall: GATE_PANELS / GATE_WALL).
// The map, search, market pages and every area's "What this means" stay open, signed in or not.
import { api, type AppConfig } from "./api.ts";
import { el } from "./dom.ts";
import { BRAND } from "../shared/brand.ts";
import { frame } from "./docframe.ts";

export type Goal = "buy" | "sell" | "invest" | "watch";
export interface Me {
  user: { email: string; goal: Goal | null; home_geo: string | null; home_name: string | null; guide: boolean; guide_step: number; created_at: string } | null;
  follows: Array<{ geo_id: string; name: string; level: string | null }>;
}
/** where a sign-up came from (the server keeps it on the account) */
export type Source = "meter" | "forecast" | "follow" | "report" | "csv" | "ask" | "paste" | "account" | "sidebar" | "market";

const GOALS: Array<[Goal, string]> = [["buy", "Buying"], ["sell", "Selling"], ["invest", "Investing"], ["watch", "Just watching"]];
const METER_KEY = "monitusre.meter.v1";
const GOAL_KEY = "monitusre.goal.v1";

let me: Me = { user: null, follows: [] };
let gate: AppConfig["gate"] = null;
let loaded: Promise<Me> | undefined;
const listeners = new Set<() => void>();

const store = {
  get<T>(k: string, d: T): T { try { const s = localStorage.getItem(k); return s ? (JSON.parse(s) as T) : d; } catch { return d; } },
  set(k: string, v: unknown) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } },
};

export const account = {
  /** Loads who's signed in (once; `force` reloads) and the gate's settings. */
  load(cfg: AppConfig | null, force = false): Promise<Me> {
    if (cfg) gate = cfg.gate ?? null;
    if (!loaded || force) loaded = fetch("/api/me", { cache: "no-store" }).then((r) => (r.ok ? r.json() : me)).catch(() => me).then((m: Me) => { me = m; emit(); return m; });
    return loaded;
  },
  get me() { return me; },
  get signedIn() { return !!me.user; },
  /** whether gated features need an account right now */
  get gated() { return !!gate && !me.user; },
  get gate() { return gate; },
  onChange(f: () => void) { listeners.add(f); return () => listeners.delete(f); },
  follows(geoId: string) { return me.follows.some((f) => f.geo_id === geoId); },
  async follow(geoId: string, on: boolean) {
    const r = await fetch("/api/me/follow", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ geo: geoId, follow: on }) });
    const b = (await r.json().catch(() => ({}))) as Me & { error?: string };
    if (!r.ok) throw new Error(b.error ?? `${r.status}`);
    me = b; emit();
  },
  async update(p: { goal?: Goal | null; guide?: boolean; home_geo?: string | null }) {
    const r = await fetch("/api/me", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(p) });
    const b = (await r.json().catch(() => ({}))) as Me & { error?: string };
    if (!r.ok) throw new Error(b.error ?? `${r.status}`);
    me = b; emit();
  },
  async signOut() {
    await fetch("/api/auth/logout", { method: "POST" });
    me = { user: null, follows: [] }; emit();
  },
  async remove() {
    const r = await fetch("/api/me/delete", { method: "POST" });
    if (!r.ok) throw new Error(`${r.status}`);
    me = { user: null, follows: [] }; emit();
  },
};
const emit = () => { for (const f of listeners) f(); };

interface MeterState { day: string; ids: string[]; dismissed: boolean }
const today = () => new Date().toLocaleDateString("en-CA"); // the visitor's own day, YYYY-MM-DD
const meterState = (): MeterState => {
  const m = store.get<Partial<MeterState>>(METER_KEY, {});
  return m.day === today() && Array.isArray(m.ids) ? { day: m.day, ids: m.ids, dismissed: !!m.dismissed } : { day: today(), ids: [], dismissed: false };
};

/**
 * The meter: counts the distinct areas whose panel was opened without an account today. "soft" (the dismissible
 * sign-up sheet) from the gate's `panels`-th area until "Not now"; "hard" (the panel blurs) from its `wall`-th area,
 * only when the server sets a wall (GATE_WALL; 0 = never, the soft launch). It starts over each day.
 */
export function meter(geoId: string): "open" | "soft" | "hard" {
  if (!account.gated || !gate) return "open";
  const m = meterState();
  if (!m.ids.includes(geoId)) { m.ids.push(geoId); store.set(METER_KEY, m); }
  const n = m.ids.indexOf(geoId) + 1;
  if (gate.wall > 0 && n >= gate.wall) return "hard";
  return n >= gate.panels && !m.dismissed ? "soft" : "open";
}
/** how many areas the meter has counted today */
export const meterCount = () => meterState().ids.length;
export const dismissMeter = () => store.set(METER_KEY, { ...meterState(), dismissed: true });

export interface SignupOpts {
  source: Source;
  /** the area it's about: the guide and the monthly update follow it */
  geo?: string | null;
  geoName?: string | null;
  /** a "Not now" button (closes the sheet) */
  dismissible?: boolean;
  onDismiss?: () => void;
  /** where to land after the link is opened (default: this page) */
  next?: string;
}

const HEADLINES: Record<Source, (area: string | null) => [string, string]> = {
  meter: () => ["Keep exploring, free", `You've looked at ${meterCount()} areas today. A free account follows the ones you care about and tells you when they turn: just your email.`],
  forecast: (a) => ["See what's driving this forecast", `The drivers behind ${a ?? "the outlook"}, how the model has done here, and what a different mortgage rate would change.`],
  follow: (a) => [`Follow ${a ?? "this area"}`, "One email a month when new data comes in: prices, supply and the outlook, with what changed."],
  report: (a) => ["Download the market report", `A printable report on ${a ?? "this area"}: valuation, buyer leverage, the outlook and the history.`],
  csv: () => ["Download the data", "CSV downloads come with a free account."],
  ask: () => ["Keep asking", gate ? `Free accounts get ${gate.askUser} questions a day.` : "Free accounts get more questions a day."],
  paste: () => ["Keep reading listings", `Free accounts get ${gate ? `${gate.pasteUser} pasted listings` : "more pasted listings"} a day. Typing the price and ZIP is always free.`],
  account: () => [`Your free ${BRAND.name} account`, "Sign up or sign in with your email: we send you a link, no password."],
  sidebar: () => [`Your free ${BRAND.name} account`, "Know when your market turns, before the headlines do."],
  market: (a) => [`Follow ${a ?? "this area"}`, "One email a month when new data comes in."],
};

let openSheet: HTMLElement | null = null;

/** The sign-up sheet: email, what you're here for, the guide box (ticked), and what an account gets you. */
export function openSignup(o: SignupOpts) {
  openSheet?.remove();
  const area = o.geoName ?? null;
  const [title, sub] = HEADLINES[o.source](area);
  let goal = store.get<Goal | null>(GOAL_KEY, null);
  const close = () => { scrim.remove(); openSheet = null; document.removeEventListener("keydown", onKey); };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && o.dismissible !== false) { close(); o.onDismiss?.(); } };
  const email = el("input", { type: "email", required: true, placeholder: "you@example.com", "aria-label": "Email address", autocomplete: "email", class: "signup-email" });
  const guide = el("input", { type: "checkbox", checked: true });
  const status = el("p", { class: "signup-status small", role: "status" });
  const submit = el("button", { class: "btn primary signup-submit", type: "submit" }, "Email me a link");
  const goalRow = el("div", { class: "signup-goals", role: "radiogroup", "aria-label": "What brings you here?" },
    ...GOALS.map(([g, label]) => el("button", { type: "button", role: "radio", class: `chip-btn ${goal === g ? "on" : ""}`, "aria-checked": String(goal === g),
      onclick: (e: Event) => {
        goal = goal === g ? null : g;
        store.set(GOAL_KEY, goal);
        for (const b of goalRow.querySelectorAll<HTMLElement>(".chip-btn")) { const on = b === e.currentTarget && !!goal; b.classList.toggle("on", on); b.setAttribute("aria-checked", String(on)); }
      } }, label)));
  const form = el("form", { class: "signup-form", onsubmit: async (e: Event) => {
    e.preventDefault();
    submit.disabled = true;
    status.textContent = "Sending…";
    try {
      const r = await fetch("/api/auth/start", { method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.value, geo: o.geo ?? undefined, goal: goal ?? undefined, guide: guide.checked, source: o.source, next: o.next ?? `${location.pathname}${location.hash}` }) });
      const b = (await r.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!r.ok) throw new Error(b.error ?? `${r.status}`);
      card.replaceChildren(
        el("button", { class: "btn icon signup-close", "aria-label": "Close", onclick: close }, "✕"),
        el("div", { class: "signup-sent" }, el("div", { class: "signup-icon", "aria-hidden": "true" }, "✉"),
          el("h2", {}, "Check your inbox"), el("p", {}, b.message ?? "We sent you a link."),
          el("p", { class: "muted small" }, "Nothing there in a minute? Check spam or promotions, or ",
            el("button", { class: "linklike", onclick: () => openSignup(o) }, "try another address"), ".")));
    } catch (err) {
      status.textContent = (err as Error).message;
      submit.disabled = false;
    }
  } },
    el("label", { class: "signup-label" }, "What brings you here?"), goalRow,
    el("div", { class: "signup-row" }, email, submit),
    el("label", { class: "signup-check small" }, guide,
      el("span", {}, area ? `Send me a short guide to ${area} (6 emails over 3 weeks), then a monthly update` : "Send me a short guide to reading the market (6 emails over 3 weeks)")),
    status);
  const card = el("div", { class: "signup-card", role: "dialog", "aria-modal": "true", "aria-labelledby": "signup-title" },
    o.dismissible === false ? null : el("button", { class: "btn icon signup-close", "aria-label": "Close", onclick: () => { close(); o.onDismiss?.(); } }, "✕"),
    el("div", { class: "logo signup-logo" }, el("img", { class: "logo-mark", src: "/favicon.svg", alt: "" }), BRAND.parts[0], el("b", {}, BRAND.parts[1])),
    el("h2", { id: "signup-title" }, title),
    el("p", { class: "signup-sub" }, sub),
    el("ul", { class: "signup-perks" },
      el("li", {}, el("b", {}, "Know when your market turns. "), `A monthly update on ${area ?? "the areas you follow"}: what changed and why the outlook moved.`),
      el("li", {}, el("b", {}, "The whole picture. "), "The forecast's drivers and track record, reports and downloads, more Ask questions and pasted listings a day."),
      el("li", {}, el("b", {}, "A 3-week guide. "), `Is ${area ?? "your area"} overpriced, who has the upper hand, where prices are headed.`)),
    form,
    el("p", { class: "muted small signup-fine" }, "Free. No password, no card. Already signed up? Same form: we'll email you a sign-in link. ",
      el("a", { href: "#/privacy", target: "_blank" }, "Privacy")),
    o.dismissible ? el("button", { class: "linklike small signup-later", onclick: () => { close(); o.onDismiss?.(); } }, "Not now") : null);
  const scrim = el("div", { class: "signup-scrim", onclick: (e: Event) => { if (e.target === scrim && o.dismissible !== false) { close(); o.onDismiss?.(); } } }, card);
  document.body.append(scrim);
  document.addEventListener("keydown", onKey);
  openSheet = scrim;
  setTimeout(() => email.focus(), 30);
}

/** After a sign-in link (?signed_in=new|1): a welcome or a short note, then the parameter leaves the address bar. */
export function welcomeBack() {
  const q = new URLSearchParams(location.search);
  const v = q.get("signed_in");
  if (!v) return;
  q.delete("signed_in");
  history.replaceState(null, "", `${location.pathname}${q.size ? `?${q}` : ""}${location.hash}`);
  void account.load(null).then((m) => {
    if (!m.user) return;
    const toast = el("div", { class: "toast", role: "status" });
    if (v === "new") {
      toast.append(el("b", {}, `Welcome to ${BRAND.name}.`), " ",
        m.user.guide ? `Your guide${m.user.home_name ? ` to ${m.user.home_name}` : ""} starts in your inbox today. ` : "",
        "Everything's unlocked. ", el("a", { href: "#/account" }, "Your account"));
    } else toast.append(el("b", {}, "You're signed in."), " ", el("a", { href: "#/account" }, "Your account"));
    toast.append(el("button", { class: "btn icon", "aria-label": "Close", onclick: () => toast.remove() }, "✕"));
    document.body.append(toast);
    setTimeout(() => toast.remove(), 12_000);
  });
}

/** The sidebar's account line: "Sign up free" or the address with a link to the account page. */
export function accountChip(area: () => { geo: string; name: string } | null) {
  const host = el("div", { class: "account-chip" });
  const render = () => host.replaceChildren(account.signedIn
    ? el("a", { class: "btn small ghost", href: "#/account", title: account.me.user!.email }, "◉ Account")
    : el("button", { class: "btn small primary", onclick: () => { const a = area(); openSignup({ source: "sidebar", dismissible: true, geo: a?.geo, geoName: a?.name }); } }, "Sign up free"));
  account.onChange(render);
  render();
  return host;
}

/** A locked section: what it holds and one button to sign up. */
export function lockedTeaser(label: string, blurb: string, o: SignupOpts) {
  return el("div", { class: "locked" },
    el("div", { class: "locked-label" }, "🔒 ", label),
    el("p", { class: "muted small" }, blurb),
    el("button", { class: "btn small primary", onclick: () => openSignup(o) }, "Unlock free"));
}

/** #/account: the signed-in account (goal, guide, follows, sign out, delete), or the sign-up form. */
export async function renderAccount(root: HTMLElement) {
  const cfg = await api.config().catch(() => null);
  await account.load(cfg, true);
  const draw = () => {
    const m = account.me;
    if (!m.user) {
      frame(root, "Account", el("div", { class: "doc-stack" }, el("div", { class: "report-card doc-body" },
        el("h1", {}, `Your free ${BRAND.name} account`),
        el("p", {}, "Sign up or sign in with your email. We send you a link: no password, no card."),
        el("button", { class: "btn primary", onclick: () => openSignup({ source: "account", next: "/#/" }) }, "Sign up or sign in"))));
      return;
    }
    const u = m.user;
    const goalSel = el("select", { "aria-label": "What brings you here", onchange: (e: Event) => void account.update({ goal: ((e.target as HTMLSelectElement).value || null) as Goal | null }) },
      el("option", { value: "" }, "—"), ...GOALS.map(([g, l]) => el("option", { value: g, selected: u.goal === g }, l)));
    const guideBox = el("input", { type: "checkbox", checked: u.guide, onchange: (e: Event) => void account.update({ guide: (e.target as HTMLInputElement).checked }) });
    let armed = false;
    const del = el("button", { class: "btn small ghost danger", onclick: async () => {
      if (!armed) { armed = true; del.textContent = "Click again to delete your account and its emails"; return; }
      await account.remove();
      location.hash = "#/";
    } }, "Delete my account");
    frame(root, "Account", el("div", { class: "doc-stack" },
      el("div", { class: "report-card doc-body account-page" },
        el("h1", {}, "Your account"),
        el("p", {}, "Signed in as ", el("b", {}, u.email), "."),
        el("div", { class: "account-row" }, el("label", {}, "I'm here for "), goalSel),
        // every email this account gets, in one place: the monthly updates (one per followed area), then the guide
        el("h2", {}, "Your emails"),
        el("h3", {}, "Monthly market updates"),
        el("p", { class: "muted small" }, "One email a month per area when new data comes in."),
        m.follows.length
          ? el("ul", { class: "account-follows" }, ...m.follows.map((f) => el("li", {},
            el("a", { href: `#?level=${f.level ?? "zip"}&metric=home_value&geo=${encodeURIComponent(f.geo_id)}` }, f.name),
            el("button", { class: "btn small ghost", "aria-label": `Stop the monthly update for ${f.name}`, onclick: () => void account.follow(f.geo_id, false) }, "Stop"))))
          : el("p", { class: "muted" }, "Open any area and press Follow to get its monthly update."),
        el("h3", {}, "Getting-started guide"),
        u.guide_step >= 6 ? el("p", { class: "muted small" }, "6 short emails over 3 weeks: you've had all 6.")
          : el("label", { class: "account-row" }, guideBox,
            ` 6 short emails over 3 weeks${u.home_name ? ` on ${u.home_name}` : ""} (${u.guide_step} of 6 sent)`),
        el("p", { class: "muted small" }, "Every email has a one-click unsubscribe link."),
        el("div", { class: "account-actions" },
          el("button", { class: "btn small", onclick: async () => { await account.signOut(); location.hash = "#/"; } }, "Sign out"),
          del))));
  };
  account.onChange(draw);
  draw();
}
